import * as Sentry from '@sentry/nextjs';
import { parseCookie } from 'cookie';
import { IncomingMessage } from 'http';
import { sleep } from 'radash';

import { publicConfig } from '@public-config';

type FetchOptions = Omit<RequestInit, 'method'> & {
    throwError?: boolean;
    hideConsoleError?: boolean;
    req?: IncomingMessage;
};

type FetchOptionsNoBody = Omit<FetchOptions, 'body'>;

type ApiLogger = {
    log: typeof console.log;
    warn: typeof console.log;
    error: typeof console.log;
};

type Failure = {
    kind: 'API' | 'Parsing' | 'Fetching';
    status?: number;
    message: string;
    error: unknown;
};

const MutedLogger: ApiLogger = {
    log: () => {},
    warn: () => {},
    error: () => {},
};

const ServerLogger: ApiLogger = {
    log: console.log,
    warn: console.warn,
    error: (message: string, context?: Parameters<typeof Sentry.captureMessage>[1]) => {
        console.warn(message);
        Sentry.captureMessage(message, { level: 'error', ...(typeof context === 'object' ? context : {}) });
    },
};

class ApiService {
    static fetch = async (
        endpoint: string,
        method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'GET',
        options: FetchOptions | undefined,
        retries = 0,
    ): Promise<any> => {
        const logger: ApiLogger = options?.hideConsoleError
            ? MutedLogger
            : typeof window === 'undefined'
              ? ServerLogger
              : console;

        const headers = new Headers({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            ...options?.headers,
        });

        if (publicConfig.environmentLockAccess) {
            headers.set('environmentLockAccess', publicConfig.environmentLockAccess);
        }

        if (options?.req) {
            const cookies = parseCookie(options.req.headers.cookie || '');

            const dcideJwt = cookies['dcide-jwt'];
            if (dcideJwt) {
                headers.set('Authorization', dcideJwt);
            }

            const environmentLockAccess = cookies['environmentLockAccess'];
            if (environmentLockAccess) {
                headers.set('environmentLockAccess', environmentLockAccess);
            }
        }

        let failure: Failure;

        try {
            const response = await fetch(endpoint, {
                ...options, // must be first
                credentials: 'include',
                method,
                headers,
                signal: options?.signal,
            });

            if (response.status === 204 /* No content */) {
                return null;
            }

            if (response.ok) {
                switch (headers.get('Accept')) {
                    case 'text/event-stream':
                        return response;
                    case 'text/markdown':
                        return response.text();
                    default:
                        return response.json();
                }
            }

            failure = await getResponseFailure(response);
        } catch (error) {
            if (retries > 0) {
                const timeout = [1500, 1000, 500][retries - 1] || 1500;
                await sleep(timeout);

                logger.warn(`Retrying ${endpoint} (${retries - 1} retries left)`);
                return ApiService.fetch(endpoint, method, options, retries - 1);
            }

            failure = { kind: 'Fetching', message: getNetworkReason(error), error };
        }

        reportFailure(logger, method, endpoint, failure);

        if (options?.throwError) {
            throw failure.error;
        }
    };

    static get = async <T = any>(endpoint: string, options: FetchOptions = {}, retries = 0) =>
        ApiService.fetch(endpoint, 'GET', options, retries) as Promise<T>;

    static post = async <T = any>(endpoint: string, json: any = {}, options: FetchOptionsNoBody = {}) =>
        ApiService.fetch(endpoint, 'POST', { body: JSON.stringify(json), ...options }) as Promise<T>;

    static patch = async <T = any>(endpoint: string, json: any = {}, options: FetchOptionsNoBody = {}) =>
        ApiService.fetch(endpoint, 'PATCH', { body: JSON.stringify(json), ...options }) as Promise<T>;

    static delete = async (endpoint: string, options: FetchOptions = {}) =>
        ApiService.fetch(endpoint, 'DELETE', options);
}

const getResponseFailure = async (response: Response): Promise<Failure> => {
    const body = await response.text();

    try {
        const { message, error, errors } = body.startsWith('{') ? JSON.parse(body) : {};
        const text = String(message || error || errors?.[0]?.message || body);

        return { kind: 'API', status: response.status, message: text, error: new Error(text) };
    } catch (error) {
        return { kind: 'Parsing', status: response.status, message: body, error };
    }
};

const getNetworkReason = (error: unknown) => {
    if (typeof window !== 'undefined' && !navigator.onLine) {
        return 'Browser reported as offline';
    }

    return error instanceof Error || error instanceof DOMException
        ? `${error.name}: ${error.message}`
        : 'Unknown error';
};

const reportFailure = (logger: ApiLogger, method: string, endpoint: string, failure: Failure) => {
    const { kind, status, message, error } = failure;
    const { pathname } = new URL(endpoint, publicConfig.urls.frontend);
    const title = `Failed to fetch (${kind}${status ? ` ${status}` : ''}): ${method} ${pathname}`;
    const expected = status === 404 || (error as Error | undefined)?.name === 'AbortError';

    if (expected) {
        logger.warn(title);
    } else {
        const route = getRoutePattern(pathname);

        logger.error(title, {
            fingerprint: ['api-fetch', kind, String(status ?? ''), method, route],
            tags: { 'api.route': route, 'api.status': status },
            extra: { endpoint, message },
        });
    }

    logger.log(JSON.stringify({ endpoint, status, message: message.slice(0, 500) }));
};

const getRoutePattern = (pathname: string) =>
    pathname
        .split('/')
        .map((segment) => (OBJECT_ID.test(segment) || PROJECT_SLUG.test(segment) ? ':id' : segment))
        .join('/');

const OBJECT_ID = /^[0-9a-f]{24}$/;

const PROJECT_SLUG = /^(?![a-z]+$)[A-Za-z0-9]{5}(:[A-Za-z0-9]{5}){0,2}$/;

export { ApiService };
